import React, { createContext, useState, useEffect } from "react";
import apiClient from "../api/Api";
import { useQueryClient } from "@tanstack/react-query";

// Create a context
export const DataContext = createContext();

export const DataProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const AUTH_USER = process.env.REACT_APP_AUTH_USER;
  const [userData, setUserData] = useState({
    email: "",
    name: "",
    projectId: [],
    role: "",
    remarks: [],
    paytypes: [],
    declinereasons: [],
  });

  const role = {
    roles_1: "user",
    roles_2: "admin",
    roles_3: "financialadmin",
    roles_4: "accountant",
    roles_5: "superadmin",
    roles_6: "administrator",
  };

  const statusDef = {
    initial: "Submitted",
    phase1: "Approved",
    phase2: "Financial Approved",
    phase3: "Uploaded to Bank",
    phase4: "Payment done,Awaiting for Bills",
    phase5: "Bills Quality Hold",
    partialPayment: "Partial Payment Done",
    final: "Bills Accepted",
    fail: "Denied",
    accountantDenied: "Accountant Denied",
    financialDenied: "Financial Admin Denied",
    qualityfail: "Bills Quality failed",
    Suspend: "Suspended",
  };

  const statusColors = {
    initial: { bg: "#808080", text: "#FFFFFF" }, // Light Grey - Submitted (White text)
    phase1: { bg: "#8B00FF", text: "#FFFFFF" }, // Purple - Approved (White text)
    phase2: { bg: "#D8BFD8", text: "#000000" }, // Indigo - Financial Approved (Black text)
    phase3: { bg: "#0000FF", text: "#FFFFFF" }, // Blue - Uploaded to Bank (White text)
    phase4: { bg: "#FFBF00", text: "#000000" }, // Amber - Payment done, Awaiting Bills (Black text)
    phase5: { bg: "#FFA500", text: "#000000" }, // Orange - Bills Quality Hold (Black text)
    partialPayment: { bg: "#00838F", text: "#FFFFFF" }, // Teal - Partial Payment Done (White text)
    final: { bg: "#008100", text: "#FFFFFF" }, // Green - Bills Accepted (White text)
    fail: { bg: "#DC143C", text: "#FFFFFF" }, // Red - Denied (White text)
    accountantDenied: { bg: "#DC143C", text: "#FFFFFF" }, // Crimson - Accountant Denied (White text)
    financialDenied: { bg: "#DC143C", text: "#FFFFFF" }, // Firebrick - Financial Admin Denied (White text)
    qualityfail: { bg: "#FF4500", text: "#FFFFFF" }, // OrangeRed - Bills Quality Failed (White text)
    suspend: { bg: "#8E24AA", text: "#FFFFFF" }, // Purple - Suspended (White text)
  };
  const storedClientId = localStorage.getItem("clientId");

  useEffect(() => {
    if (storedClientId) {
      setUserData((prevUserData) => ({
        ...prevUserData,
      }));

      // If another component (signin) has just triggered an explicit fetch,
      // skip the automatic fetch to avoid duplicate requests.
      let skip = false;
      try {
        skip = !!sessionStorage.getItem("skipAutoFetch");
      } catch (e) {
        skip = false;
      }

      if (!skip && !userData.email) {
        fetchData(storedClientId);
      }
    }
  }, [storedClientId, userData.email]);

  const fetchData = async (email) => {
    const response = await apiClient.get(`/${AUTH_USER}/getuser/${email}`);
    if (response.status !== 200) {
      setLoading(false);
      return response;
    }
    setUserData(response.data.userData);
    setLoading(false);
    return response;
  };

  // Clear data if clientId is removed from localStorage (logout scenario)
  useEffect(() => {
    if (!storedClientId) {
      queryClient.clear();
      setUserData({
        email: "",
        name: "",
        projectId: [],
        role: "",
      });
    }
  }, [storedClientId]);

  return (
    <DataContext.Provider
      value={{
        userData,
        loading,
        error,
        fetchData,
        statusDef,
        role,
        statusColors,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};
